import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  Package,
  ArrowDownToLine,
  ArrowUpFromLine,
  MapPin,
  Warehouse,
  Settings,
  ClipboardList,
  UserCog,
  Nfc,
  Shield,
  Boxes,
  ClipboardCheck,
  Printer,
  LayoutGrid,
  Truck,
  CarFront,
  AlertTriangle,
  Building2,
  Trash2,
  ArrowRightLeft,
  Clock,
  SlidersHorizontal,
  RotateCcw,
  ChevronRight,
  BookOpen,
} from "lucide-react"
import { useAuth } from "@/contexts/auth-context"
import { useTranslations } from "@/i18n"
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"

interface MenuItem {
  labelKey: string
  icon: typeof Package
  href: string
  children?: MenuItem[]
  permissions?: string[]
}

interface MenuSection {
  labelKey: string
  items: MenuItem[]
}

export function Sidebar() {
  const pathname = useLocation().pathname
  const { user, isLoading, hasAnyPermission } = useAuth()
  const t = useTranslations("nav")
  const tBrand = useTranslations("brand")

  // Don't show WMS sidebar for system admins
  if (!isLoading && user?.isSystemAdmin) return null

  const menuStructure: MenuSection[] = [
    {
      labelKey: "outbound",
      items: [
        { labelKey: "salesOrders", icon: BookOpen, href: "/sales-orders", permissions: ["orders.view", "orders.manage"] },
        { labelKey: "outboundOrders", icon: ArrowUpFromLine, href: "/outbound-shipments", permissions: ["orders.view", "orders.manage"] },
        { labelKey: "warehouseTasks", icon: ClipboardCheck, href: "/warehouse-tasks" },
      ],
    },
    {
      labelKey: "inbound",
      items: [
        { labelKey: "inboundOrders", icon: ArrowDownToLine, href: "/inbound-receiving", permissions: ["inbound.view", "inbound.manage"] },
        { labelKey: "returns", icon: RotateCcw, href: "/returns", permissions: ["inbound.view", "inbound.manage"] },
        { labelKey: "putaways", icon: Warehouse, href: "/putaway", permissions: ["putaway.view", "putaway.manage"] },
      ],
    },
    {
      labelKey: "inventoryManagement",
      items: [
        { labelKey: "inventory", icon: Package, href: "/inventory", permissions: ["inventory.view", "inventory.adjust"] },
        { labelKey: "locationInventory", icon: MapPin, href: "/inventory/locations", permissions: ["warehouses.view"] },
        { labelKey: "stockTake", icon: ClipboardCheck, href: "/stocktake", permissions: ["stocktake.view", "stocktake.manage"] },
        { labelKey: "missingItems", icon: AlertTriangle, href: "/inventory/missing", permissions: ["inventory.view"] },
        { labelKey: "disposal", icon: Trash2, href: "/inventory/disposal", permissions: ["disposal.view", "disposal.manage"] },
        { labelKey: "relocation", icon: ArrowRightLeft, href: "/inventory/relocation", permissions: ["relocation.view", "relocation.manage"] },
        { labelKey: "expiry", icon: Clock, href: "/inventory/expiry", permissions: ["expiry.view"] },
      ],
    },
    {
      labelKey: "reportsSection",
      items: [
        { labelKey: "reportsInbound", icon: ArrowDownToLine, href: "/reports/inbound", permissions: ["reports.view"] },
        { labelKey: "reportsOutbound", icon: ArrowUpFromLine, href: "/reports/outbound", permissions: ["reports.view"] },
        { labelKey: "reportsInventory", icon: Package, href: "/reports/inventory", permissions: ["reports.view"] },
        { labelKey: "reportsMovement", icon: ArrowRightLeft, href: "/reports/movement", permissions: ["reports.view"] },
        { labelKey: "reportsStockCard", icon: BookOpen, href: "/reports/stock-card", permissions: ["reports.view"] },
        { labelKey: "reportsStocktake", icon: ClipboardCheck, href: "/reports/stocktake", permissions: ["reports.view"] },
      ],
    },
    {
      labelKey: "printing",
      items: [
        { labelKey: "printJobs", icon: Printer, href: "/print-queue", permissions: ["print.view", "print.manage"] },
      ],
    },
    {
      labelKey: "settings",
      items: [
        { labelKey: "general", icon: Settings, href: "/settings/general", permissions: ["settings.manage"] },
        { labelKey: "driversVehicles", icon: CarFront, href: "/settings/dispatch", permissions: ["settings.manage"] },
        {
          labelKey: "products",
          icon: ClipboardList,
          href: "/master-list",
          permissions: ["products.view", "products.manage"],
          children: [
            { labelKey: "masterData", icon: ClipboardList, href: "/master-list", permissions: ["products.view", "products.manage"] },
            { labelKey: "attributes", icon: SlidersHorizontal, href: "/settings/products/attributes", permissions: ["products.view", "products.manage"] },
          ],
        },
        { labelKey: "suppliers", icon: Truck, href: "/suppliers", permissions: ["inbound.view", "inbound.manage"] },
        { labelKey: "customers", icon: Building2, href: "/customers", permissions: ["orders.view", "orders.manage"] },
        { labelKey: "locations", icon: LayoutGrid, href: "/settings/locations", permissions: ["warehouses.view", "zones.manage"] },
        { labelKey: "users", icon: UserCog, href: "/users", permissions: ["users.view", "users.create", "users.edit"] },
        { labelKey: "nfcCards", icon: Nfc, href: "/nfc-cards", permissions: ["users.view", "users.edit"] },
        { labelKey: "printingConfig", icon: Printer, href: "/settings", permissions: ["labels.view", "labels.manage"] },
        ...(user?.isSystemAdmin
          ? [{ labelKey: "adminDashboard", icon: Shield, href: "/admin" }]
          : []),
      ],
    },
  ]

  const isItemVisible = (item: MenuItem): boolean => {
    if (!item.permissions) return true
    if (user?.isSystemAdmin) return true
    return hasAnyPermission(...item.permissions)
  }

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/"
    if (href === "/inventory") return pathname === "/inventory"
    if (href === "/settings") return pathname === "/settings"
    return pathname === href || pathname.startsWith(href + "/")
  }

  return (
    <ShadcnSidebar
      collapsible="icon"
      className="group-data-[side=left]:border-r-0"
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg">
              <Link to="/" className="flex items-center gap-3">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary/10">
                  <Boxes className="size-5 text-primary" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="font-semibold truncate">{tBrand("name")}</span>
                  <span className="text-xs text-sidebar-foreground/60 truncate">
                    {tBrand("tagline")}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {menuStructure.map((section) => {
          const visibleItems = section.items.filter(isItemVisible)
          if (visibleItems.length === 0) return null

          return (
            <SidebarGroup key={section.labelKey}>
              <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>
              <SidebarMenu>
                {visibleItems.map((item) =>
                  item.children ? (
                    <MenuItemWithChildren
                      key={item.labelKey}
                      item={item}
                      isItemVisible={isItemVisible}
                      isActive={isActive}
                      t={t}
                    />
                  ) : (
                    <SidebarMenuItem key={item.labelKey}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(item.href)}
                        tooltip={t(item.labelKey)}
                      >
                        <Link to={item.href}>
                          <item.icon />
                          <span>{t(item.labelKey)}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ),
                )}
              </SidebarMenu>
            </SidebarGroup>
          )
        })}
      </SidebarContent>
    </ShadcnSidebar>
  )
}

function MenuItemWithChildren({
  item,
  isItemVisible,
  isActive,
  t,
}: {
  item: MenuItem
  isItemVisible: (item: MenuItem) => boolean
  isActive: (href: string) => boolean
  t: (key: string) => string
}) {
  const visibleChildren = (item.children ?? []).filter(isItemVisible)
  const anyChildActive = visibleChildren.some((c) => isActive(c.href))
  const [open, setOpen] = useState(anyChildActive)

  if (visibleChildren.length === 0) return null

  return (
    <Collapsible
      asChild
      open={open}
      onOpenChange={setOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={anyChildActive}
            tooltip={t(item.labelKey)}
          >
            <item.icon />
            <span>{t(item.labelKey)}</span>
            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {visibleChildren.map((child) => (
              <SidebarMenuSubItem key={child.labelKey}>
                <SidebarMenuSubButton asChild isActive={isActive(child.href)}>
                  <Link to={child.href}>
                    <child.icon />
                    <span>{t(child.labelKey)}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
